import type { CurrentUser, Permission, Role, ScopeType, UserStatusFilter } from '@occ/contracts';
import { useCan, useGetIdentity } from '@refinedev/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { describeHttpError } from '../../api/http';
import { ErrorNotice } from '../../components/ErrorNotice';
import {
  Alert,
  Badge,
  Button,
  Card,
  CodeBlock,
  ConfirmDialog,
  EmptyState,
  Field,
  Loading,
  Modal,
  Notice,
  Select,
  Toasts,
  useToasts,
} from '../../components/ui';
import {
  createUser,
  deleteUser,
  grantPermission,
  listUsers,
  resetUserPassword,
  revokePermission,
  updateUser,
  type UserFilters,
  type UserRecord,
} from '../../data/admin';
import { useHierarchy } from '../../hierarchy/HierarchyProvider';

/**
 * Usuarios y permisos (RF-08, Módulo 9).
 *
 * El permiso del OCC es la tupla `(usuario, scopeType, scopeId, rol)` y se
 * evalúa **en cada acción**, no al entrar. Esta pantalla es la única que la
 * escribe, así que es la más sensible del panel: es la que cambia lo que otros
 * pueden hacer.
 *
 * Cuatro decisiones que se ven en la interfaz, y que son la cara visible de las
 * cuatro reglas que aplica el backend:
 *
 * - **el alcance se elige por nombre, no por uuid.** Pegar un identificador a
 *   mano es la forma más fácil de conceder acceso al cliente equivocado, y un
 *   uuid mal copiado no da error: da un permiso que no cubre nada, o que cubre
 *   otra cosa.
 * - **se avisa de lo que un permiso arrastra.** `tenant` cubre todo lo que
 *   cuelga del inquilino, y `tenant_admin` incluye editar permisos: conceder eso
 *   es conceder la capacidad de concederse más.
 * - **sobre uno mismo no se opera.** El rol, el estado, el alcance y la propia
 *   contraseña se cambian desde otro sitio o los cambia otro administrador. Los
 *   botones salen apagados con el motivo, y si alguien los fuerza el backend
 *   responde 403 igual.
 * - **la contraseña temporal se enseña una vez.** No se manda correo desde el
 *   OCC; el diálogo la muestra para copiarla y no vuelve a estar disponible.
 */

const ROLES: Array<{ value: Role; label: string; blurb: string }> = [
  {
    value: 'read_only',
    label: 'Solo lectura',
    blurb: 'Ve inventario, salud y bitácora dentro de su alcance. Ninguna acción.',
  },
  {
    value: 'support_technician',
    label: 'Técnico de soporte',
    blurb: 'Opera nodos: pantalla, terminal, scripts, archivos, servicios.',
  },
  {
    value: 'tenant_admin',
    label: 'Administrador del tenant',
    blurb: 'Todo lo anterior más jerarquía, usuarios, permisos y branding.',
  },
];

const ROLE_LABEL: Record<Role, string> = {
  read_only: 'Solo lectura',
  support_technician: 'Técnico',
  tenant_admin: 'Administrador',
};

const SCOPE_LABEL: Record<ScopeType, string> = {
  tenant: 'Todo el tenant',
  client: 'Un cliente',
  branch: 'Una sucursal',
  node: 'Un nodo',
};

const STATUS_LABEL: Record<UserStatusFilter, string> = {
  all: 'Activos e inactivos',
  active: 'Solo activos',
  inactive: 'Solo inactivos',
};

type GrantDraft = { user: UserRecord; scopeType: ScopeType; scopeId: string; role: Role };
type RoleDraft = { user: UserRecord; role: Role };
/** Credencial recién emitida. Se enseña una vez y no se guarda en ningún sitio. */
type IssuedPassword = { email: string; password: string; created: boolean };

export function UsersPage() {
  const { data: canManage } = useCan({ resource: 'users', action: 'edit' });
  const { data: me } = useGetIdentity<CurrentUser>();
  const { tree, nodes } = useHierarchy();

  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [filters, setFilters] = useState<UserFilters>({ status: 'all' });
  const [search, setSearch] = useState('');

  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    email: '',
    name: '',
    password: '',
    scopeType: 'client' as ScopeType,
    scopeId: '',
    role: 'support_technician' as Role,
  });
  const [grant, setGrant] = useState<GrantDraft | null>(null);
  const [roleChange, setRoleChange] = useState<RoleDraft | null>(null);
  const [removing, setRemoving] = useState<UserRecord | null>(null);
  const [resetting, setResetting] = useState<UserRecord | null>(null);
  const [issued, setIssued] = useState<IssuedPassword | null>(null);

  const toasts = useToasts();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await listUsers(filters));
      setError(null);
    } catch (cause) {
      setUsers([]);
      setError(cause);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Nombres legibles de cada alcance posible, por tipo. */
  const scopeOptions = useMemo(() => {
    const clients = tree?.clients ?? [];
    return {
      tenant: [{ id: me?.tenantId ?? '', label: 'Este tenant' }],
      client: clients.map((client) => ({ id: client.id, label: client.name })),
      branch: clients.flatMap((client) =>
        client.branches.map((branch) => ({
          id: branch.id,
          label: `${client.name} · ${branch.name}`,
        })),
      ),
      node: nodes.map((node) => ({
        id: node.id,
        label: `${node.clientName} · ${node.branchName} · ${node.alias}`,
      })),
    } satisfies Record<ScopeType, Array<{ id: string; label: string }>>;
  }, [tree, nodes, me?.tenantId]);

  const labelForScope = (permission: Permission): string => {
    const match = scopeOptions[permission.scopeType].find(
      (option) => option.id === permission.scopeId,
    );
    // Si el alcance apunta a algo que este usuario no ve, se enseña el uuid
    // recortado: esconderlo haría creer que el permiso no existe.
    return match?.label ?? `${permission.scopeType} ${permission.scopeId.slice(0, 8)}…`;
  };

  /** Toda operación de escritura sigue el mismo camino: avisar, refrescar, contar. */
  const perform = async (what: string, operation: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    try {
      await operation();
      toasts.ok(what);
      await load();
      return true;
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitUser = async () => {
    setBusy(true);
    try {
      const created = await createUser({
        email: draft.email,
        name: draft.name,
        password: draft.password || undefined,
        permissions: draft.scopeId
          ? [{ scopeType: draft.scopeType, scopeId: draft.scopeId, role: draft.role }]
          : [],
      });

      setCreating(false);
      setDraft({ ...draft, email: '', name: '', password: '' });

      if (created.temporaryPassword) {
        setIssued({ email: created.email, password: created.temporaryPassword, created: true });
      } else {
        toasts.ok('Usuario creado. Tendrá que cambiar la contraseña al entrar.');
      }
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const submitGrant = async () => {
    if (!grant) return;
    const done = await perform('Permiso concedido.', () =>
      grantPermission(grant.user.id, grant.scopeType, grant.scopeId, grant.role).then(() => {}),
    );
    if (done) setGrant(null);
  };

  const submitRoleChange = async () => {
    if (!roleChange) return;
    const done = await perform('Rol actualizado en todos sus alcances.', () =>
      updateUser(roleChange.user.id, { role: roleChange.role }).then(() => {}),
    );
    if (done) setRoleChange(null);
  };

  const toggleActive = (user: UserRecord) =>
    perform(user.isActive ? 'Usuario desactivado.' : 'Usuario reactivado.', () =>
      updateUser(user.id, { isActive: !user.isActive }).then(() => {}),
    );

  const revoke = (user: UserRecord, permission: Permission) =>
    perform('Permiso revocado.', () =>
      revokePermission(user.id, permission.scopeType, permission.scopeId),
    );

  const submitRemoval = async () => {
    if (!removing) return;
    const done = await perform('Usuario borrado.', () => deleteUser(removing.id));
    if (done) setRemoving(null);
  };

  const submitReset = async () => {
    if (!resetting) return;
    setBusy(true);
    try {
      const { temporaryPassword } = await resetUserPassword(resetting.id);
      setIssued({ email: resetting.email, password: temporaryPassword, created: false });
      setResetting(null);
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const isSelf = (user: UserRecord): boolean => user.id === me?.id;

  return (
    <section className="grid gap-4">
      <Toasts toasts={toasts.toasts} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Usuarios y permisos</h1>
          <p className="text-sm opacity-60">
            El permiso es (usuario, alcance, rol) y se comprueba en cada acción.
          </p>
        </div>
        <Button
          variant="primary"
          disabled={!canManage?.can}
          title={canManage?.can ? undefined : canManage?.reason}
          onClick={() => setCreating(true)}
        >
          Nuevo usuario
        </Button>
      </div>

      {!canManage?.can && (
        <Alert>{canManage?.reason ?? 'Solo un administrador del tenant gestiona usuarios.'}</Alert>
      )}

      {/* ── Filtros ── */}
      <Card title="Filtrar">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            label="Correo o nombre"
            value={search}
            placeholder="Parte del correo o del nombre"
            onChange={(event) => setSearch(event.target.value)}
            // Se filtra al soltar el foco o con Enter y no en cada tecla: cada
            // cambio es una consulta al backend.
            onBlur={() => setFilters({ ...filters, search: search.trim() || undefined })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setFilters({ ...filters, search: search.trim() || undefined });
              }
            }}
          />

          <Select
            label="Rol"
            value={filters.role ?? ''}
            onChange={(event) =>
              setFilters({
                ...filters,
                role: (event.target.value || undefined) as Role | undefined,
              })
            }
          >
            <option value="">Cualquier rol</option>
            {ROLES.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>

          <Select
            label="Estado"
            value={filters.status ?? 'all'}
            onChange={(event) =>
              setFilters({ ...filters, status: event.target.value as UserStatusFilter })
            }
          >
            {(Object.keys(STATUS_LABEL) as UserStatusFilter[]).map((status) => (
              <option key={status} value={status}>
                {STATUS_LABEL[status]}
              </option>
            ))}
          </Select>

          <div className="grid gap-2">
            <Select
              label="Alcance"
              value={filters.scopeType ?? ''}
              onChange={(event) => {
                const scopeType = (event.target.value || undefined) as ScopeType | undefined;
                setFilters({ ...filters, scopeType, scopeId: undefined });
              }}
            >
              <option value="">Cualquier alcance</option>
              {(Object.keys(SCOPE_LABEL) as ScopeType[]).map((scopeType) => (
                <option key={scopeType} value={scopeType}>
                  {SCOPE_LABEL[scopeType]}
                </option>
              ))}
            </Select>

            {filters.scopeType && (
              <Select
                label="Sobre"
                value={filters.scopeId ?? ''}
                onChange={(event) =>
                  setFilters({ ...filters, scopeId: event.target.value || undefined })
                }
              >
                <option value="">Cualquiera de ese nivel</option>
                {scopeOptions[filters.scopeType].map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </div>
        </div>

        {filters.scopeType && filters.scopeId && (
          <p className="mt-3 text-xs opacity-70">
            Con un alcance concreto la lista responde quién lo <strong>alcanza</strong>: sale
            también quien llega por un permiso más amplio, como un administrador del cliente entero.
          </p>
        )}
      </Card>

      {error !== null && <ErrorNotice error={error} what="Listar los usuarios" />}

      {loading && users.length === 0 ? (
        <Loading label="Cargando usuarios…" />
      ) : users.length === 0 ? (
        <Card>
          <EmptyState title="Sin usuarios que enseñar">
            {error !== null
              ? 'La consulta falló; corrige el motivo de arriba.'
              : 'Ningún operador cumple el filtro. Prueba con «Activos e inactivos» y sin alcance.'}
          </EmptyState>
        </Card>
      ) : (
        users.map((user) => (
          <Card
            key={user.id}
            title={
              <span className="inline-flex flex-wrap items-center gap-2">
                {user.name || user.email}
                {user.highestRole && (
                  <Badge tone={user.highestRole === 'tenant_admin' ? 'warn' : 'neutral'}>
                    {ROLE_LABEL[user.highestRole]}
                  </Badge>
                )}
                {user.isActive ? (
                  <Badge tone="ok">activo</Badge>
                ) : (
                  <Badge tone="warn">inactivo</Badge>
                )}
                {user.mfaEnabled ? (
                  <Badge tone="ok">MFA</Badge>
                ) : (
                  <Badge tone="warn">sin MFA</Badge>
                )}
                {user.mustChangePassword && <Badge tone="warn">contraseña temporal</Badge>}
                {isSelf(user) && <Badge>tú</Badge>}
              </span>
            }
            actions={
              <div className="flex flex-wrap gap-1">
                <Button
                  variant="ghost"
                  disabled={!canManage?.can || busy || isSelf(user)}
                  title={isSelf(user) ? 'No puedes cambiar tu propio alcance' : undefined}
                  onClick={() =>
                    setGrant({
                      user,
                      scopeType: 'client',
                      scopeId: scopeOptions.client[0]?.id ?? '',
                      role: 'support_technician',
                    })
                  }
                >
                  Conceder alcance
                </Button>
                <Button
                  variant="ghost"
                  disabled={
                    !canManage?.can || busy || isSelf(user) || user.permissions.length === 0
                  }
                  title={
                    isSelf(user)
                      ? 'No puedes cambiarte el rol a ti mismo'
                      : user.permissions.length === 0
                        ? 'Concédele primero un alcance: el rol se aplica sobre lo que ya tiene'
                        : undefined
                  }
                  onClick={() => setRoleChange({ user, role: user.highestRole ?? 'read_only' })}
                >
                  Cambiar rol
                </Button>
                <Button
                  variant="ghost"
                  disabled={!canManage?.can || busy || isSelf(user)}
                  title={isSelf(user) ? 'No puedes desactivarte a ti mismo' : undefined}
                  onClick={() => void toggleActive(user)}
                >
                  {user.isActive ? 'Desactivar' : 'Reactivar'}
                </Button>
                <Button
                  variant="ghost"
                  disabled={!canManage?.can || busy || isSelf(user)}
                  title={
                    isSelf(user)
                      ? 'Tu propia contraseña se cambia desde tu perfil, no desde aquí'
                      : undefined
                  }
                  onClick={() => setResetting(user)}
                >
                  Restablecer contraseña
                </Button>
                <Button
                  variant="danger"
                  disabled={!canManage?.can || busy || isSelf(user)}
                  title={isSelf(user) ? 'No puedes borrar tu propia cuenta' : undefined}
                  onClick={() => setRemoving(user)}
                >
                  Borrar
                </Button>
              </div>
            }
          >
            <div className="grid gap-2">
              <div className="text-sm opacity-70">
                {user.email}
                {user.lastLoginAt && (
                  <span className="opacity-60">
                    {' · '}último acceso {user.lastLoginAt.toLocaleString('es')}
                  </span>
                )}
                {!user.lastLoginAt && <span className="opacity-60">{' · '}nunca ha entrado</span>}
              </div>

              {!user.isActive && (
                <Notice tone="warn">
                  Desactivado: no puede iniciar sesión. Los tokens que ya tuviera siguen valiendo
                  hasta que caduquen —minutos—, así que si la cuenta está comprometida hay que
                  restablecer también la contraseña.
                </Notice>
              )}

              {user.permissions.length === 0 ? (
                <Notice tone="warn">
                  Sin permisos: este usuario puede entrar pero no ve ningún nodo.
                </Notice>
              ) : (
                <ul className="grid gap-1">
                  {user.permissions.map((permission) => (
                    <li
                      key={`${permission.scopeType}:${permission.scopeId}:${permission.role}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded border border-black/10 dark:border-white/10 px-2.5 py-1.5 text-sm"
                    >
                      <span className="inline-flex flex-wrap items-center gap-2">
                        <Badge tone={permission.role === 'tenant_admin' ? 'warn' : 'neutral'}>
                          {ROLE_LABEL[permission.role]}
                        </Badge>
                        <span className="opacity-50">sobre</span>
                        <span>{labelForScope(permission)}</span>
                      </span>
                      <Button
                        variant="ghost"
                        disabled={!canManage?.can || busy || isSelf(user)}
                        title={isSelf(user) ? 'No puedes revocarte tu propio alcance' : undefined}
                        onClick={() => void revoke(user, permission)}
                      >
                        Revocar
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        ))
      )}

      {/* ── Nuevo usuario ── */}
      <Modal
        open={creating}
        title="Nuevo usuario"
        onClose={() => setCreating(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={
                busy ||
                draft.email.trim() === '' ||
                draft.name.trim() === '' ||
                (draft.password !== '' && draft.password.length < 12)
              }
              onClick={() => void submitUser()}
            >
              {busy ? 'Creando…' : 'Crear'}
            </Button>
          </>
        }
      >
        <div className="grid gap-3">
          <Field
            label="Correo"
            type="email"
            value={draft.email}
            autoFocus
            onChange={(event) => setDraft({ ...draft, email: event.target.value })}
          />
          <Field
            label="Nombre"
            value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          />
          <Field
            label="Contraseña inicial (opcional)"
            type="password"
            value={draft.password}
            hint="Déjala vacía y el OCC genera una temporal que se enseña una sola vez. Mínimo 12 caracteres si la pones tú."
            onChange={(event) => setDraft({ ...draft, password: event.target.value })}
          />

          <Select
            label="Alcance inicial"
            value={draft.scopeType}
            onChange={(event) => {
              const scopeType = event.target.value as ScopeType;
              setDraft({ ...draft, scopeType, scopeId: '' });
            }}
          >
            {(Object.keys(SCOPE_LABEL) as ScopeType[]).map((scopeType) => (
              <option key={scopeType} value={scopeType}>
                {SCOPE_LABEL[scopeType]}
              </option>
            ))}
          </Select>

          <Select
            label="Sobre"
            value={draft.scopeId}
            onChange={(event) => setDraft({ ...draft, scopeId: event.target.value })}
          >
            <option value="">Sin alcance por ahora</option>
            {scopeOptions[draft.scopeType].map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>

          <Select
            label="Rol en ese alcance"
            value={draft.role}
            onChange={(event) => setDraft({ ...draft, role: event.target.value as Role })}
          >
            {ROLES.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>

          {draft.scopeId === '' && (
            <Notice tone="warn" title="Sin alcance">
              Entra al panel y no ve nada. Además, un alta sin alcance solo la acepta el backend si
              administras el tenant completo: nadie más podría volver a ver esa cuenta.
            </Notice>
          )}
        </div>
      </Modal>

      {/* ── Conceder alcance ── */}
      <Modal
        open={grant !== null}
        title={`Conceder alcance a ${grant?.user.email ?? ''}`}
        onClose={() => setGrant(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setGrant(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={busy || (grant?.scopeId ?? '') === ''}
              onClick={() => void submitGrant()}
            >
              {busy ? 'Concediendo…' : 'Conceder'}
            </Button>
          </>
        }
      >
        {grant && (
          <div className="grid gap-3">
            <Select
              label="Nivel del alcance"
              value={grant.scopeType}
              onChange={(event) => {
                const scopeType = event.target.value as ScopeType;
                setGrant({
                  ...grant,
                  scopeType,
                  // Al cambiar de nivel, el identificador anterior no vale: apunta
                  // a otra clase de objeto.
                  scopeId: scopeOptions[scopeType][0]?.id ?? '',
                });
              }}
            >
              {(Object.keys(SCOPE_LABEL) as ScopeType[]).map((scopeType) => (
                <option key={scopeType} value={scopeType}>
                  {SCOPE_LABEL[scopeType]}
                </option>
              ))}
            </Select>

            <Select
              label="Sobre"
              value={grant.scopeId}
              onChange={(event) => setGrant({ ...grant, scopeId: event.target.value })}
            >
              {scopeOptions[grant.scopeType].length === 0 && (
                <option value="">Nada que elegir</option>
              )}
              {scopeOptions[grant.scopeType].map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Select
              label="Rol"
              value={grant.role}
              onChange={(event) => setGrant({ ...grant, role: event.target.value as Role })}
            >
              {ROLES.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </Select>

            <p className="text-xs opacity-70">
              {ROLES.find((role) => role.value === grant.role)?.blurb}
            </p>

            {grant.user.permissions.some(
              (existing) =>
                existing.scopeType === grant.scopeType && existing.scopeId === grant.scopeId,
            ) && (
              <Notice tone="pending">
                Ya tiene un permiso sobre ese alcance: esto lo sustituye, no añade un segundo.
              </Notice>
            )}

            {grant.scopeType === 'tenant' && (
              <Notice tone="warn" title="Alcance de tenant">
                Cubre todos los clientes, sucursales y nodos, presentes y futuros. Un cliente nuevo
                queda incluido sin que nadie lo decida otra vez.
              </Notice>
            )}
            {grant.role === 'tenant_admin' && (
              <Notice tone="warn" title="Administrador del tenant">
                Incluye editar permisos dentro de ese alcance: quien lo tenga puede conceder ahí
                todo lo que él mismo tiene.
              </Notice>
            )}
          </div>
        )}
      </Modal>

      {/* ── Cambiar rol ── */}
      <Modal
        open={roleChange !== null}
        title={`Cambiar el rol de ${roleChange?.user.email ?? ''}`}
        onClose={() => setRoleChange(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRoleChange(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button variant="primary" disabled={busy} onClick={() => void submitRoleChange()}>
              {busy ? 'Cambiando…' : 'Cambiar rol'}
            </Button>
          </>
        }
      >
        {roleChange && (
          <div className="grid gap-3">
            <Select
              label="Rol"
              value={roleChange.role}
              onChange={(event) =>
                setRoleChange({ ...roleChange, role: event.target.value as Role })
              }
            >
              {ROLES.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </Select>

            <p className="text-xs opacity-70">
              {ROLES.find((role) => role.value === roleChange.role)?.blurb}
            </p>

            <Notice tone="pending" title="Se aplica a todo lo que ya tiene">
              El rol se reescribe en sus {roleChange.user.permissions.length} concesión
              {roleChange.user.permissions.length === 1 ? '' : 'es'} actuales; los alcances no
              cambian. Para que entre en un sitio nuevo, concédele ese alcance.
            </Notice>

            <ul className="grid gap-1 text-sm">
              {roleChange.user.permissions.map((permission) => (
                <li
                  key={`${permission.scopeType}:${permission.scopeId}`}
                  className="rounded border border-black/10 dark:border-white/10 px-2.5 py-1.5"
                >
                  {labelForScope(permission)}
                  <span className="opacity-50">
                    {' · '}
                    {ROLE_LABEL[permission.role]} → {ROLE_LABEL[roleChange.role]}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>

      {/* ── Contraseña emitida: se enseña una sola vez ── */}
      <Modal
        open={issued !== null}
        title="Contraseña temporal"
        onClose={() => setIssued(null)}
        footer={
          <Button variant="primary" onClick={() => setIssued(null)}>
            Ya la he entregado
          </Button>
        }
      >
        {issued && (
          <div className="grid gap-3">
            <p className="text-sm">
              {issued.created ? 'Cuenta creada para ' : 'Contraseña restablecida para '}
              <strong>{issued.email}</strong>. Tendrá que cambiarla al entrar.
            </p>
            <CodeBlock value={issued.password} label="Contraseña de un solo uso" />
            <Notice tone="warn" title="No se vuelve a mostrar">
              El OCC solo guarda su hash. Entrégala por el canal que ya uses con esa persona: no se
              manda ningún correo desde aquí.
            </Notice>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={resetting !== null}
        title="Restablecer contraseña"
        confirmLabel="Restablecer"
        danger={false}
        busy={busy}
        onCancel={() => setResetting(null)}
        onConfirm={() => void submitReset()}
      >
        <p>
          Se genera una contraseña temporal para <strong>{resetting?.email}</strong> y la actual
          deja de valer inmediatamente.
        </p>
        <Notice tone="warn">
          Sus sesiones abiertas siguen vivas hasta que caduque el token —minutos—. Si el motivo es
          una cuenta comprometida, desactívala además de restablecerla.
        </Notice>
      </ConfirmDialog>

      <ConfirmDialog
        open={removing !== null}
        title="Borrar usuario"
        confirmLabel="Borrar"
        confirmText={removing?.email}
        busy={busy}
        onCancel={() => setRemoving(null)}
        onConfirm={() => void submitRemoval()}
      >
        <p>
          <strong>{removing?.email}</strong> perderá el acceso al panel y a MCP.
        </p>
        <Notice tone="warn">
          Su bitácora se conserva —es un registro append-only— pero las entradas quedarán con un
          identificador sin nombre. Si solo quieres retirarle acceso, desactívalo o revoca sus
          permisos: eso se deshace.
        </Notice>
      </ConfirmDialog>
    </section>
  );
}
